import type { IStorageForecastChartModule } from '@/interfaces/powerMarkets/IModule.ts';
import type {
    IDashboardStorageForecastCountryOverrides,
    IDashboardStorageForecastContext,
    TDashboardStorageForecastOverrideKind,
} from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import type { IRunListItem } from '@/interfaces/storageForecasting/IRunListItem.ts';
import type { IAssumptionDetails } from '@/interfaces/storageForecasting/IAssumptionDetails.ts';
import type { IAssetInputDto } from '@/interfaces/storageForecasting/IAsset.ts';
import type { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';
import { DEFAULT_PERCENTILES } from '@/constants/forecasting/assetConstants.ts';

export type TStorageForecastReportSourceType = NonNullable<IStorageForecastChartModule['data']['sourceType']>;

export interface IStorageForecastContextSelection extends IDashboardStorageForecastContext {
    sourceType?: TStorageForecastReportSourceType;
    inputId?: string;
    inputLabel?: string;
    runId?: string;
    runLabel?: string;
}

export interface IStorageForecastRunContextOption extends IStorageForecastContextSelection {
    sourceType: 'RunOutput';
    inputId: string;
    inputLabel: string;
    country: TStorageForecastCountries;
    quarter: string;
    scenarioId: string;
    scenarioName: string;
    scenarioLabel: string;
    runId: string;
    runLabel: string;
}

export interface IStorageForecastAssumptionContextOption extends IStorageForecastContextSelection {
    sourceType: 'SystemAssumptions' | 'SystemForecasts';
    country: TStorageForecastCountries;
    quarter: string;
    scenarioId: string;
    scenarioName: string;
    scenarioLabel: string;
}

export function getStorageForecastOverrideContexts (
    override: IDashboardStorageForecastCountryOverrides | null | undefined,
): Array<IDashboardStorageForecastContext> {
    if (!override) return [];
    return Object.values(override).filter((context): context is IDashboardStorageForecastContext => !!context);
}

export function getStorageForecastOverrideForCountry (
    override: IDashboardStorageForecastCountryOverrides | null | undefined,
    country: TStorageForecastCountries | null | undefined,
): IDashboardStorageForecastContext | null {
    return country ? override?.[country] ?? null : null;
}

function normaliseId (value: string | number | null | undefined): string {
    return value === null || value === undefined ? '' : String(value);
}

function getScenarioName (scenario: { id?: string | number | null; name?: string | number | null }): string {
    return normaliseId(scenario.name) || normaliseId(scenario.id);
}

function uniqueBy<T> (items: Array<T>, getKey: (item: T) => string): Array<T> {
    const seen = new Set<string>();
    return items.filter((item) => {
        const key = getKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

export function buildStorageForecastRunContextOptions (runList: { runList: Record<string, IRunListItem> }): Array<IStorageForecastRunContextOption> {
    const options: Array<IStorageForecastRunContextOption> = [];

    Object.entries(runList.runList).forEach(([inputId, input]) => {
        Object.entries(input.quarterToScenarios).forEach(([quarter, scenarioGroup]) => {
            scenarioGroup.scenarios.forEach((scenario) => {
                const scenarioId = normaliseId(scenario.id);
                const scenarioName = getScenarioName(scenario);
                const runLabel = `${input.name} - ${quarter}_${scenarioName}`;
                options.push({
                    sourceType: 'RunOutput',
                    inputId,
                    inputLabel: input.name,
                    country: input.country,
                    quarter,
                    scenarioId,
                    scenarioName,
                    scenarioLabel: scenarioName,
                    runId: scenarioId,
                    runLabel,
                });
            });
        });
    });

    return options;
}

export function buildStorageForecastAssetContextOptions (
    inputs: ReadonlyArray<IAssetInputDto>,
): Array<IStorageForecastRunContextOption> {
    return inputs.flatMap(input => input.runs
        .filter(run => run.isCompleted || run.runState === 'Completed')
        .map((run) => {
            const scenarioName = normaliseId(run.scenario);
            return {
                sourceType: 'RunOutput' as const,
                inputId: input.id,
                inputLabel: input.data.name,
                country: input.data.country,
                quarter: run.quarter,
                scenarioId: run.id,
                scenarioName,
                scenarioLabel: scenarioName,
                runId: run.id,
                runLabel: `${input.data.name} - ${run.quarter}_${scenarioName}`,
            };
        }));
}

export function buildStorageForecastAssumptionContextOptions (
    assumptionList: Array<IAssumptionDetails>,
    sourceType: 'SystemAssumptions' | 'SystemForecasts',
): Array<IStorageForecastAssumptionContextOption> {
    return assumptionList.flatMap((assumption) => assumption.scenarios.map((scenario) => {
        const scenarioId = normaliseId(scenario.id);
        const scenarioName = getScenarioName(scenario);

        return {
            sourceType,
            country: assumption.country,
            quarter: assumption.quarter,
            scenarioId,
            scenarioName,
            scenarioLabel: scenarioName,
        };
    }));
}

export function getStorageForecastOverrideKind (
    sourceType: IStorageForecastChartModule['data']['sourceType'],
): TDashboardStorageForecastOverrideKind {
    return sourceType === 'RunOutput' ? 'asset' : 'system';
}

export function getStorageForecastContextSummary (
    context: IStorageForecastContextSelection | IDashboardStorageForecastContext | null | undefined,
    sourceType: IStorageForecastChartModule['data']['sourceType'],
    includeInput = false,
): string {
    if (!context) return '';

    const scenario = context.scenarioName || context.scenarioLabel || '';
    const percentile = context.percentile
        ? DEFAULT_PERCENTILES.find(option => option.id === context.percentile)?.name ?? context.percentile
        : '';
    const parts = sourceType === 'RunOutput'
        ? [
            includeInput ? (context as IStorageForecastContextSelection).inputLabel : '',
            context.country,
            context.quarter,
            scenario,
            percentile,
        ]
        : [
            context.country,
            context.quarter,
            scenario,
        ];

    return parts.filter(Boolean).join(' | ');
}

export function findStorageForecastRunContextOption (
    options: Array<IStorageForecastRunContextOption>,
    context: IStorageForecastContextSelection | IDashboardStorageForecastContext | null | undefined,
    fixedInputId?: string,
): IStorageForecastRunContextOption | null {
    if (!context) return null;
    const inputId = fixedInputId || (context as IStorageForecastContextSelection).inputId;

    return options.find(option => (
        (!inputId || option.inputId === inputId)
        && (!context.country || option.country === context.country)
        && (!context.quarter || option.quarter === context.quarter)
        && (
            (!!(context as IStorageForecastContextSelection).runId && option.runId === (context as IStorageForecastContextSelection).runId)
            || (!!context.scenarioId && option.scenarioId === context.scenarioId)
            || (!!context.scenarioName && option.scenarioName === context.scenarioName)
            || (!!context.scenarioLabel && option.scenarioLabel === context.scenarioLabel)
            || (!context.scenarioId && !context.scenarioName && !context.scenarioLabel && !(context as IStorageForecastContextSelection).runId)
        )
    )) ?? null;
}

export function findStorageForecastAssumptionContextOption (
    options: Array<IStorageForecastAssumptionContextOption>,
    context: IStorageForecastContextSelection | IDashboardStorageForecastContext | null | undefined,
): IStorageForecastAssumptionContextOption | null {
    if (!context) return null;

    return options.find(option => (
        (!context.country || option.country === context.country)
        && (!context.quarter || option.quarter === context.quarter)
        && (
            (!!context.scenarioId && option.scenarioId === context.scenarioId)
            || (!!context.scenarioName && option.scenarioName === context.scenarioName)
            || (!!context.scenarioLabel && option.scenarioLabel === context.scenarioLabel)
            || (!context.scenarioId && !context.scenarioName && !context.scenarioLabel)
        )
    )) ?? null;
}

export function getUniqueStorageForecastInputs (options: Array<IStorageForecastRunContextOption>): Array<{ id: string; name: string }> {
    return uniqueBy(options.map(option => ({ id: option.inputId, name: option.inputLabel })), option => option.id);
}

export function getUniqueStorageForecastCountries<T extends { country: TStorageForecastCountries }> (options: Array<T>): Array<{ id: TStorageForecastCountries; name: TStorageForecastCountries }> {
    return uniqueBy(options.map(option => ({ id: option.country, name: option.country })), option => option.id);
}

export function getUniqueStorageForecastQuarters<T extends { quarter: string }> (options: Array<T>): Array<{ id: string; name: string }> {
    return uniqueBy(options.map(option => ({ id: option.quarter, name: option.quarter })), option => option.id);
}

export function getUniqueStorageForecastScenarios<T extends { scenarioName?: string; scenarioLabel?: string; scenarioId?: string }> (options: Array<T>): Array<{ id: string; name: string }> {
    return uniqueBy(options.map((option) => {
        const name = option.scenarioName || option.scenarioLabel || option.scenarioId || '';
        return { id: option.scenarioId || name, name };
    }), option => option.id || option.name);
}
